/**
 * What the drawer can do with files and their folders: name a folder (new or renamed) or a file
 * (renamed), the right-click entries, and putting a file on the graph. Plain functions and one
 * signal; the requests go to the assets service.
 */
import { createSignal, type Accessor } from "solid-js";
import type { AssetInfo } from "@protocol/AssetInfo";
import type { AssetSource } from "@protocol/AssetSource";
import { SEPARATOR, type MenuEntry } from "../../state/menu";
import type { AssetsService } from "../../state/assets";
import type { NodeAdder, PagePoint } from "../../state/nodeAdder";
import { assetName, assetStem, embedOf, joinPath, parentOf } from "../../shared/assets";
import { toast } from "../../shared/toast";
import type { Spot } from "../../shared/spot";

/** A name being typed: a new folder inside `parent`, or `renaming` an asset or folder. */
export interface FileNaming {
  /** The source. */
  source: AssetSource;
  /** Where the new folder goes, or where the renamed one is. */
  parent: string;
  /** What is renamed (`null`: a new folder). */
  renaming: string | null;
}

/** The drawer's file tools. */
export interface FileTools {
  /** The name being typed, or `null`. */
  naming: Accessor<FileNaming | null>;
  /** Starts naming a new folder inside `parent`. */
  startNew(source: AssetSource, parent: string): void;
  /** Starts renaming `path` (an asset or a folder). */
  startRename(source: AssetSource, path: string): void;
  /** Ends typing with `name` (empty: nothing). */
  finish(name: string): Promise<void>;
  /** Stops typing. */
  cancel(): void;
  /** The right-click entries of a folder (`""`: the source's top). */
  folderMenu(source: AssetSource, path: string): MenuEntry[];
  /** The right-click entries of an asset. */
  fileMenu(source: AssetSource, asset: AssetInfo): MenuEntry[];
  /** Makes a node attached to the asset (the Add node window opens for a node at `spot`, beside `near`). */
  toGraph(source: AssetSource, asset: AssetInfo, spot: Spot | null, near: PagePoint | null): Promise<void>;
  /** Asks for files of the computer to keep in `folder` (browses it, then opens the gallery's file field). */
  pick(source: AssetSource, folder: string): void;
}

/** What the tools use. */
export interface FileToolsDeps {
  assets: AssetsService;
  nodeAdder: NodeAdder;
  /** Closes the drawer (a file went onto the graph). */
  placed?: () => void;
}

/** Starts the file tools of one browser. */
export function createFileTools({ assets, nodeAdder, placed }: FileToolsDeps): FileTools {
  const [naming, setNaming] = createSignal<FileNaming | null>(null);
  /** Refuses (with a toast) a source that cannot be changed. */
  const writable = (source: AssetSource) => {
    const found = assets.source(source);
    if (found?.readOnly) toast(found.why, "error");
    return !!found && !found.readOnly;
  };
  const tools: FileTools = {
    naming,
    startNew(source, parent) {
      if (writable(source)) setNaming({ source, parent, renaming: null });
    },
    startRename(source, path) {
      if (writable(source)) setNaming({ source, parent: parentOf(path), renaming: path });
    },
    async finish(name) {
      const now = naming();
      setNaming(null);
      const clean = name.trim();
      if (!now || !clean || clean.includes("/")) {
        if (clean.includes("/")) toast("A name cannot hold a /.", "error");
        return;
      }
      if (now.renaming === null) {
        if (await assets.makeFolder(now.source, joinPath(now.parent, clean))) assets.go({ source: now.source, folder: joinPath(now.parent, clean) });
      } else if (clean !== assetName(now.renaming)) {
        await assets.move(now.source, now.renaming, joinPath(now.parent, clean));
      }
    },
    cancel: () => setNaming(null),
    folderMenu(source, path) {
      const entries: MenuEntry[] = [
        { label: "Upload files here…", action: () => tools.pick(source, path) },
        { label: "New folder", action: () => tools.startNew(source, path) },
      ];
      if (path) {
        entries.push({ label: "Rename", action: () => tools.startRename(source, path) });
        entries.push(SEPARATOR, { label: "Delete the folder and its files", danger: true, confirm: "Click again to delete it all", action: () => void assets.remove(source, path) });
      }
      return entries;
    },
    fileMenu(source, asset) {
      const entries: MenuEntry[] = [
        { label: "Preview", action: () => assets.preview({ source, path: asset.path }) },
        { label: "Download", action: () => void assets.download(source, asset.path) },
        { label: "Make a node of it", action: () => void tools.toGraph(source, asset, null, null) },
      ];
      if (source === "project") entries.push({ label: "Copy what a page writes to show it", action: () => void navigator.clipboard?.writeText(embedOf(asset.path)).then(() => toast(`Copied ${embedOf(asset.path)}: paste it in a page.`)) });
      else entries.push({ label: "Copy into the project", action: () => void assets.copy(source, asset.path, "project", "") });
      entries.push(
        { label: "Rename", action: () => tools.startRename(source, asset.path) },
        SEPARATOR,
        { label: "Delete", danger: true, confirm: "Click again to delete it", action: () => void assets.remove(source, asset.path) },
      );
      return entries;
    },
    async toGraph(source, asset, spot, near) {
      let path = asset.path;
      if (source !== "project") {
        const copy = await assets.copy(source, asset.path, "project", "");
        if (!copy) return;
        path = copy.path;
      }
      nodeAdder.open(spot?.hint ?? null, near, null, assetStem(path));
      nodeAdder.attach([path]);
      nodeAdder.placeAt(spot);
      placed?.();
    },
    pick(source, folder) {
      if (!writable(source)) return;
      // The gallery's file field (`#asset-upload`) keeps what is picked in the folder browsed.
      assets.go({ source, folder });
      queueMicrotask(() => document.querySelector<HTMLInputElement>("#asset-upload")?.click());
    },
  };
  return tools;
}
